import type {
  Bootstrap, PriorityEntry, ScenarioEvent, ScenarioItem, YnabReconcile, YnabMappingRow, YnabCategoryActual,
  YnabItemMappingRow, MappableExpense,
  BudgetIntentView, BudgetProviderView, PushResult, AllocationView,
} from '@shared/types';

/**
 * How the API client gets a Clerk session token.
 *
 * `api` is a plain module, not a hook, and it's called from stores and effects
 * all over the app — so it can't call `useAuth()`. Instead the provider registers
 * a getter once, and every request asks for a fresh token. Clerk rotates these
 * roughly every minute, so caching one here would produce sporadic 401s that
 * only reproduce after a minute of idling.
 */
/** What a full refresh reports back. `ynab` is null when it isn't connected. */
export interface RefreshResponse {
  ok: true;
  counts: Record<string, number>;
  durationMs: number;
  ynab: { categoryCount: number } | null;
  /** Set when Notion synced but YNAB didn't — a partial success, not a failure. */
  ynabError: string | null;
}

/** Subscription state for the Notion push endpoint. */
export interface NotionWebhookStatus {
  configured: boolean;
  /** Paste this into Notion's integration UI to complete verification. */
  verificationToken: string | null;
  verifiedAt: string | null;
  lastEventAt: string | null;
  lastEventType: string | null;
  lastError: string | null;
  /** Events have arrived that no sync has folded in yet. */
  pending: boolean;
  lastWebhookSyncAt: string | null;
  endpoint: string;
}

type TokenGetter = () => Promise<string | null>;
let getToken: TokenGetter | null = null;

export function setAuthTokenGetter(fn: TokenGetter | null): void {
  getToken = fn;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken ? await getToken() : null;
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status} ${path}: ${body.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  me: () => req<{ email: string; environment: string }>('/me'),
  bootstrap: () => req<Bootstrap>('/bootstrap'),

  /**
   * Make everything current: Notion, then YNAB reconciled against it.
   *
   * There is deliberately no Notion-only variant on the client. Two refresh
   * buttons that had to be pressed in the right order is what let the two
   * halves of a comparison drift apart.
   */
  refresh: () =>
    req<RefreshResponse>('/refresh', { method: 'POST' }),
  syncStatus: () =>
    req<{ lastRunAt: string | null; status: string | null; ageMinutes: number | null }>('/sync/status'),
  notionWebhook: () => req<NotionWebhookStatus>('/webhooks/notion'),

  ynabReconcile: () => req<YnabReconcile>('/ynab/reconcile'),
  ynabMapping: () => req<{ mapping: YnabMappingRow[]; categories: YnabCategoryActual[] }>('/ynab/mapping'),
  putYnabMapping: (notionGroup: string, ynabCategoryIds: string[]) =>
    req(`/ynab/mapping/${encodeURIComponent(notionGroup)}`, { method: 'PUT', body: JSON.stringify({ ynabCategoryIds }) }),
  ynabItemMapping: () => req<{
    itemMapping: YnabItemMappingRow[];
    ynabCategories: YnabCategoryActual[];
    expenses: MappableExpense[];
  }>('/ynab/item-mapping'),
  putYnabItemMapping: (ynabCategoryId: string, expenseIds: string[], ignored = false) =>
    req(`/ynab/item-mapping/${encodeURIComponent(ynabCategoryId)}`, {
      method: 'PUT', body: JSON.stringify({ expenseIds, ignored }),
    }),
  putNotTrackedInYnab: (expenseId: string, notTracked: boolean) =>
    req(`/ynab/not-tracked/${encodeURIComponent(expenseId)}`, {
      method: 'PUT', body: JSON.stringify({ notTracked }),
    }),
  /**
   * The Funding page's "Refresh now" — a full refresh, same as Settings.
   *
   * `ynabOnly` is for confirming a target write landed, where re-reading
   * Notion would only add latency to a figure that cannot have changed.
   */
  ynabRefresh: (ynabOnly = false) =>
    // Partial, because the `ynabOnly` path returns just the YNAB half — and so
    // does the full path when NOTION_TOKEN is unset.
    req<Partial<RefreshResponse> & { ok: true; categoryCount: number }>(
      `/ynab/refresh${ynabOnly ? '?ynabOnly=1' : ''}`, { method: 'POST' },
    ),

  // ── budget providers, writes and history ──
  budgetProviders: () => req<{ providers: BudgetProviderView[] }>('/budget/providers'),
  putBudgetProvider: (id: string, patch: { enabled?: boolean; writesEnabled?: boolean }) =>
    req(`/budget/providers/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(patch) }),
  disconnectProvider: (id: string) =>
    req(`/budget/providers/${encodeURIComponent(id)}/disconnect`, { method: 'POST' }),
  /** Push one or more stale targets. The server re-derives every number. */
  budgetPushTargets: (ynabCategoryIds: string[]) =>
    req<{ batchId: string; results: PushResult[] }>('/budget/targets', {
      method: 'POST', body: JSON.stringify({ items: ynabCategoryIds.map(ynabCategoryId => ({ ynabCategoryId })) }),
    }),
  /**
   * Assign one or more amounts this month. The server reads what's currently
   * assigned and adds each amount on top, logging every line as its own
   * revertable intent.
   */
  budgetAssign: (items: { ynabCategoryId: string; amount: number }[]) =>
    req<{ batchId: string; results: PushResult[] }>('/budget/assign', {
      method: 'POST', body: JSON.stringify({ items }),
    }),
  budgetIntents: (subjectKey?: string) =>
    req<{ intents: BudgetIntentView[] }>(
      `/budget/intents${subjectKey ? `?subjectKey=${encodeURIComponent(subjectKey)}` : ''}`),
  budgetAllocation: () => req<AllocationView>('/budget/allocation'),
  budgetApplyAllocation: (buckets: string[]) =>
    req<{ batchId: string; results: { bucket: string; status: string; reason?: string }[] }>(
      '/budget/allocation', { method: 'POST', body: JSON.stringify({ buckets }) }),
  budgetRevertIntent: (id: number) =>
    req<{ ok: true; intentId: number; status: string }>(`/budget/intents/${id}/revert`, { method: 'POST' }),

  putDials: (id: string, dials: Record<string, number>) =>
    req(`/scenarios/${id}/dials`, { method: 'PUT', body: JSON.stringify(dials) }),
  putEvents: (id: string, events: ScenarioEvent[]) =>
    req(`/scenarios/${id}/events`, { method: 'PUT', body: JSON.stringify(events) }),
  putPriority: (id: string, priority: PriorityEntry[]) =>
    req(`/scenarios/${id}/priority`, { method: 'PUT', body: JSON.stringify(priority) }),
  putScenarioItems: (id: string, items: ScenarioItem[]) =>
    req(`/scenarios/${id}/items`, { method: 'PUT', body: JSON.stringify(items) }),
  patchScenario: (id: string, patch: Record<string, unknown>) =>
    req(`/scenarios/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  createScenario: (name: string, cloneFrom?: string) =>
    req<{ id: string }>('/scenarios', { method: 'POST', body: JSON.stringify({ name, cloneFrom }) }),
  deleteScenario: (id: string) => req(`/scenarios/${id}`, { method: 'DELETE' }),

  settings: () => req<Record<string, string>>('/settings'),
  putSettings: (patch: Record<string, string>) =>
    req('/settings', { method: 'PUT', body: JSON.stringify(patch) }),
};
